import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';
import { useCrLinkDaLimulo } from '@/hooks/useCrLinkDaLimulo';
import { useMeQuery } from '@/services/qbotApi';
import { Segmented } from '@/components/common/Segmented';
import { MyDecksMode } from './decks/MyDecksMode';
import { MetaDecksMode } from './decks/MetaDecksMode';
import { TrendMetaMode } from './decks/TrendMetaMode';
import { DeckBuilderMode } from './decks/DeckBuilderMode';
import { DeckBuilderSheet } from './decks/DeckBuilderSheet';
import type { CRPlayerCard } from '@/types/qbot';

// Upgrades + the "Suggested" build list (BuildableDecksMode) are hidden for now
// — their components stay in the tree so they can be re-enabled without a rebuild.
type Mode = 'topDecks' | 'myDecks' | 'buildable' | 'trend';

/**
 * Decks — Top Decks (the meta) and My Decks (your current deck + saved decks).
 *
 * Replaces an embed of /cr/decks/popular that opened on other people's decks
 * and reached 8 of the 20 CR endpoints the app has wired. Two tabs now: the
 * meta browser, and a paged view of your own decks (current deck first, saved
 * decks after) folding in the deck rating (/cr/decks/check), the saved list
 * (/cr/decks/saved) and the AI builder (/cr/decks/builder).
 *
 * Declares `ownScroll` in HubSections: the tab switcher is pinned and only the
 * body below it moves — same contract as the tournament tabs.
 */
export function DecksPanel() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const navigate = useNavigate();
  const { data: identity, error: identityErr } = useMeQuery();

  const isPro = identity?.tier === 'pro' || identity?.tier === 'admin';
  // ⚠️ Non trattarlo come COLLEGATO quando `/cr/me` ha FALLITO, che era il caso muto: con
  // `identity` undefined `notLinked` e' falso, l'invito a collegare il tag spariva e `isPro`
  // era falso — cioe' lo schermo diceva «collegato, tier free», che e' un'affermazione, e su
  // un 401 e' falsa. I sotto-modi poi dicono il vero, ma la riga di stato in cima no.
  //
  // ⛔ Si discrimina sullo STATUS, non su `isError` nudo, e la differenza e' visibile solo in
  // un caso che oggi non capita: un **404 `NOT_LINKED`** non e' un guasto, e' la risposta
  // «non hai un tag collegato». Con `isError` nudo avrebbe mostrato la riga rossa E fatto
  // sparire l'invito a collegarsi — due bugie invece di una verita'. Rilevato in revisione
  // il 2026-09-06 su un percorso non ancora vivo: qbot oggi risponde 200.
  const identityStatus = (identityErr as { status?: number } | undefined)?.status;
  /** Il 404 di qbot: non un guasto, la risposta «non hai un tag collegato». */
  const identityMissing = identityStatus === 404;

  // 🔴 Con Limulo come fonte «collegato» lo decide il profilo CR di LIMULO, non `/cr/me`: il
  // Sit&Go scrive li' (`POST /v1/games/profiles`) e QBot legge una tabella sua, quindi chi
  // aveva collegato dal Sit&Go vedeva ancora «Collega». `/cr/me` resta per il TIER (`isPro`).
  // Il banner compare SOLO se il profilo manca a lettura riuscita; se la lettura fallisce si
  // dice che non si e' potuto leggere — «non ho potuto chiedere» non e' «non collegato».
  // Fuori dal ramo (`kind: 'off'`) tutto qui sotto e' identico a prima.
  const crLink = useCrLinkDaLimulo();
  const fromLimulo = crLink.kind !== 'off';
  const linkHref = crLink.kind === 'notLinked' ? crLink.createHref : '/cr/link';
  // Piu' di un profilo CR attivo: QBot non sceglie per ordine di lista e la TMA non sa disattivarne
  // uno. Non e' «collegato» ne' «non collegato»: si dice il fatto, senza «Collega».
  const piuProfili = crLink.kind === 'unknown' && crLink.motivo === 'piuProfili' ? crLink.n : null;

  // Undefined while /me is in flight — don't treat someone as unlinked before asking.
  const notLinked = fromLimulo
    ? crLink.kind === 'notLinked'
    : (identity != null && identity.linkedCrTag == null) || identityMissing;
  const identityUnknown = fromLimulo
    ? crLink.kind === 'unknown'
    : identityErr != null && !identityMissing && identity == null;

  // Linked players land on their own decks; unlinked players can't (no tag), so
  // they land on Top Decks — which needs none — rather than being bounced to
  // /cr/link out of the Hub.
  const [mode, setMode] = useState<Mode>('myDecks');
  const [cardFilter, setCardFilter] = useState<string[]>([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  // Only My Decks is forced/blocked without a linked tag; Build carries its own
  // link CTA, and Top Decks + Trend are global meta.
  const active: Mode = notLinked && mode === 'myDecks' ? 'topDecks' : mode;

  // Order: Top Decks · Trend · Build · My Decks.
  const MODES: { key: Mode; label: string }[] = [
    { key: 'topDecks', label: t('panel.tab.topDecks') },
    { key: 'trend', label: t('panel.tab.trend') },
    { key: 'buildable', label: t('panel.tab.buildable') },
    { key: 'myDecks', label: t('panel.tab.myDecks') },
  ];
  const needsTag = (m: Mode) => m === 'myDecks';

  /**
   * Tap a card on your current deck → Top Decks, filtered by it.
   *
   * The filter takes card NAMES (the server compares against `card.name`), and
   * your own deck is the card picker this filter always needed.
   */
  const pickCard = (card: CRPlayerCard) => {
    haptics.selection();
    setCardFilter((prev) => (prev.includes(card.name) ? prev : [...prev, card.name]));
    setMode('topDecks');
  };

  return (
    <div className="relative flex h-full flex-col gap-2.5">
      {/* Le quattro voci ci stanno tutte: segmentato, non chip separate. */}
      <Segmented
        options={MODES.map((m) => ({
          value: m.key,
          label: m.label,
          disabled: notLinked && needsTag(m.key),
        }))}
        value={active}
        onChange={setMode}
      />

      {identityUnknown && (
        <p className="shrink-0 rounded-lg border border-danger/40 bg-danger/10 px-2.5 py-2 text-[11px] leading-snug text-fg-muted">
          {piuProfili !== null
            ? t('crLink.moreProfiles', { n: piuProfili })
            : t('panel.identity.unknown', {
                defaultValue:
                  "Couldn't read your Clash Royale account. What you see below is the public meta, not your decks.",
              })}
        </p>
      )}

      {notLinked && (
        <div className="flex shrink-0 items-center justify-between gap-2 rounded-lg border border-brand/40 bg-brand/10 px-2.5 py-2">
          <p className="min-w-0 text-[11px] leading-snug text-fg-muted">{t('panel.link.prompt')}</p>
          <button
            type="button"
            onClick={() => {
              haptics.impact('light');
              navigate(linkHref);
            }}
            className="shrink-0 rounded-md bg-brand px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-fg"
          >
            {t('panel.link.cta')}
          </button>
        </div>
      )}

      {/* No scroller: each mode is a fixed single-deck stage that fills this box
          and never reflows between pages. overflow-hidden clips (the deck +
          stats size themselves to fit) and keeps the horizontal axis — the one
          the Hub reads as "next section" — from ever becoming scrollable. */}
      {/* NIENTE card di sfondo qui, a differenza delle altre sezioni.
          I modi interni (Top Deck, Trend, Build) non scorrono: impaginano su
          un'altezza fissa e si dimensionano da soli per riempire questo box.
          Una superficie attorno li stringeva e il contenuto ne usciva. La
          leggibilità sul motivo a scaglie andrà risolta sui singoli elementi,
          non con un contenitore — è il lavoro sui tasti ancora da fare. */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {active === 'topDecks' && (
          <MetaDecksMode
            isPro={isPro}
            cardFilter={cardFilter}
            onRemoveCard={(name) => setCardFilter((prev) => prev.filter((n) => n !== name))}
            onClearCards={() => setCardFilter([])}
          />
        )}
        {active === 'myDecks' && (
          <MyDecksMode isPro={isPro} onPickCard={pickCard} onBuild={() => setBuilderOpen(true)} />
        )}
        {active === 'trend' && <TrendMetaMode />}
        {active === 'buildable' && <DeckBuilderMode />}
      </div>

      {builderOpen && env.featureCrAiEnabled && (
        <DeckBuilderSheet isPro={isPro} onClose={() => setBuilderOpen(false)} />
      )}
    </div>
  );
}
